import { createClient } from "@supabase/supabase-js";
import type { Database } from "./supabase.types";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

// Security check: Prevent using secret key in client-side code
if (supabaseAnonKey) {
  // Check for new Supabase secret key format (starts with sb_secret_)
  if (supabaseAnonKey.startsWith("sb_secret_")) {
    throw new Error(
      "🚨 SECURITY ERROR: You are using a SECRET key in client-side code!\n\n" +
        "This is a CRITICAL security vulnerability. The secret key has full database access.\n\n" +
        "SOLUTION:\n" +
        "1. Go to Supabase Dashboard > Settings > API Keys\n" +
        "2. Copy the PUBLISHABLE key (starts with sb_publishable_)\n" +
        "3. Update VITE_SUPABASE_ANON_KEY in your .env.local file\n\n" +
        "The secret key should ONLY be used in server-side code (Edge Functions)."
    );
  }

  // Check for legacy service_role key (JWT format)
  if (
    supabaseAnonKey.includes("service_role") ||
    supabaseAnonKey.match(/^eyJ.*service_role/)
  ) {
    throw new Error(
      "🚨 SECURITY ERROR: You are using a SERVICE_ROLE key in client-side code!\n\n" +
        "This is a CRITICAL security vulnerability. The service_role key bypasses all security.\n\n" +
        "SOLUTION:\n" +
        "1. Go to Supabase Dashboard > Settings > API Keys\n" +
        "2. Copy the PUBLISHABLE key (or legacy anon key)\n" +
        "3. Update VITE_SUPABASE_ANON_KEY in your .env.local file\n\n" +
        "The service_role key should ONLY be used in server-side code (Edge Functions)."
    );
  }
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey);

export async function fetchPublicServices(shopKey = "default") {
  return supabase
    .from("services")
    .select(
      "id,name,description,base_price,duration_minutes,landing_price,landing_duration,landing_order,landing_short_description"
    )
    .eq("shop_key", shopKey)
    .eq("is_public", true)
    .eq("is_deleted", false)
    .order("landing_order", { ascending: true })
    .order("name", { ascending: true });
}

export async function trackBookingRequest(
  trackingToken: string,
  phoneLastDigits?: string
) {
  // Use direct fetch to properly handle 403 responses with JSON body
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  
  try {
    const response = await fetch(`${supabaseUrl}/functions/v1/booking-request-track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({ 
        trackingToken,
        ...(phoneLastDigits && { phoneLastDigits }),
      }),
    });
    
    const data = await response.json();
    
    // Return in Supabase client format
    if (response.ok) {
      return { data, error: null };
    } else {
      // For non-2xx responses, still return the data (contains error info)
      return { 
        data, 
        error: new Error(`Edge Function returned status ${response.status}`)
      };
    }
  } catch (err) {
    return { 
      data: null, 
      error: err as Error
    };
  }
}

export async function submitBookingRating({
  trackingToken,
  serviceRating,
  serviceComment,
  workerRating,
  workerComment,
}: {
  trackingToken: string;
  serviceRating?: number;
  serviceComment?: string;
  workerRating?: number;
  workerComment?: string;
}) {
  const body: Record<string, unknown> = {
    trackingToken,
  };

  if (serviceRating) {
    body.serviceRating = serviceRating;
    body.rating = serviceRating;
  }
  if (typeof serviceComment === "string" && serviceComment.trim().length > 0) {
    body.serviceComment = serviceComment.trim();
    body.comment = serviceComment.trim();
  }

  if (workerRating) {
    body.workerRating = workerRating;
  }
  if (typeof workerComment === "string" && workerComment.trim().length > 0) {
    body.workerComment = workerComment.trim();
  }

  return supabase.functions.invoke("booking-rating-create", {
    body,
  });
}

export function subscribeToBookingRequest(
  trackingToken: string,
  onUpdate: (payload: any) => void
) {
  if (!trackingToken) return () => {};

  const channel = supabase.channel(`booking_request:${trackingToken}`, {
    config: {
      broadcast: {
        ack: false,
        self: false,
      },
    },
  });

  channel.on("broadcast", { event: "status_update" }, (payload) => {
    const snapshot = (payload as any)?.payload ?? payload;
    if (typeof onUpdate === "function") {
      onUpdate(snapshot);
    }
  });

  channel.subscribe();

  return () => {
    channel.unsubscribe();
    supabase.removeChannel(channel);
  };
}
